import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useNotifications,
  useReadNotifications,
} from '@/hooks/api/useNotifications';
import { NotificationItem } from './NotificationItem';

export const NotificationPanel = () => {
  const { notifications, unreadCount } = useNotifications();
  const { markReadNotification, clearAllNotifications } =
    useReadNotifications();

  const hasNotifications =
    Array.isArray(notifications) && notifications.length > 0;

  const handleOpenChange = (open: boolean) => {
    if (open && unreadCount > 0) markReadNotification();
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-white relative hover:bg-accent rounded-xl transition-colors"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-background">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-96 p-0 overflow-hidden overflow-y-scroll rounded-xl shadow-xl border border-border"
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">
              Notifications
            </span>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center h-5 px-1.5 rounded-full bg-red-500/10 text-red-500 text-[10px] font-semibold">
                {unreadCount} new
              </span>
            )}
          </div>
          {hasNotifications && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearAllNotifications?.()}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground gap-1.5"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Clear all
            </Button>
          )}
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        {!hasNotifications ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <BellOff className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">All caught up</p>
            <p className="text-xs text-muted-foreground mt-1">
              No notifications right now
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[420px]">
            <div className="py-1">
              {notifications.map((notification, index) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  isLast={index === notifications.length - 1}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
